import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import {
  type FloorPlans,
  getMediaOptions,
  type ProjectLayoutType,
  useMedia,
  useProjectMedia,
} from '@/api/media';
import { closeModal, openModal } from '@/components/modals/modals.utils';
import { useDeleteFloorLayouts } from '@/features/estate-media/api/delete-floor-layouts';
import { useUploadFloorLayouts } from '@/features/estate-media/api/upload-floor-layout';
import { useEstate } from '@/features/estates-property/api/estate';

import { ACCEPTED_FLOORPLANS_INPUT_TYPE, uploadLayoutsSchema } from '../schema/upload-media';
import { MediaData } from './media-data';
import type { UploadMediaModalType } from './upload-media-modal';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/media/floor-plans'
);

export const FloorPlansDashboard = () => {
  const { estateId } = route.useParams();
  const { data } = useMedia({ id: estateId });
  const { data: estate } = useEstate({ id: estateId });
  const { data: projectMedia } = useProjectMedia({ id: estate?.projectId });

  const dataFloorLayouts = useMemo(() => {
    const dates = new Set<string>([
      ...Object.keys(data.floorLayouts),
      ...Object.keys(projectMedia.layouts),
    ]);
    const groupedImages = Array.from(dates).reduce<
      Record<string, FloorPlans[string] | ProjectLayoutType[]>
    >((acc, date) => {
      const estateFloorLayouts = data.floorLayouts[date] || [];
      const projectFloorLayouts = projectMedia.layouts[date] || [];
      acc[date] = [...estateFloorLayouts, ...projectFloorLayouts];
      return acc;
    }, {});
    return groupedImages;
  }, [data, projectMedia]);

  const { mutate: uploadFloorLayouts } = useUploadFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey });
      },
    },
  });

  const { mutate: deleteFloorLayouts } = useDeleteFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey });
      },
    },
  });

  const submitHandler = async (data: UploadMediaModalType) => {
    // Convert File[] to FileList
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadFloorLayouts({ layouts: dataTransfer.files, estateId });
    closeModal();
  };

  const handleOpenModal = () => {
    openModal('upload-media', {
      submitHandler,
      acceptedType: ACCEPTED_FLOORPLANS_INPUT_TYPE,
      acceptedMediaSchema: uploadLayoutsSchema(),
    });
  };

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const handleDelele = (selectedIds: string[]) => {
    openModal('confirmation-modal', {
      title: `${t('delete')} ${t('image')} `,
      description: t('confirmDeleteDocument'),
      confirmationText: t('delete'),
      onSubmit: async () => await deleteFloorLayouts({ estateId, layoutIds: selectedIds }),
    });
  };

  return (
    <MediaData
      data={dataFloorLayouts}
      onUpload={handleOpenModal}
      handleDelete={handleDelele}
      page="floor-plans"
      type="estate"
      estateId={estateId}
    />
  );
};
